/**
 * DepartmentHealthCard — single cell in the 21-department grid.
 *
 * Layout (Notion-strict):
 *   - 13px department name (semibold) + role caption (12px muted)
 *   - tiny status dot (green / yellow / red / gray) inline with name
 *   - row of 2 KPIs (pending tasks, throughput 24h)
 *   - hairline border, rounded-4px, hover bg-muted/70
 *
 * Click → /departments/:deptKey detail page. Hover surfaces a kebab
 * "More" affordance that opens the actions menu (currently just
 * "Sync workflow" + "Open queue" — extendable).
 */
import type { DepartmentSummary } from '@chaos/types/department';
import { Link } from '@tanstack/react-router';
import { MoreHorizontal } from 'lucide-react';

import { cn } from '../../lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { fmtCount, fmtRelative, KpiTile } from './kpi-tile';

interface DepartmentHealthCardProps {
  department: DepartmentSummary;
}

const STATE_DOT_CLASS: Record<DepartmentSummary['health'], string> = {
  green: 'bg-success',
  yellow: 'bg-warning',
  red: 'bg-destructive',
  unknown: 'bg-muted-foreground/40',
};

const STATE_LABEL: Record<DepartmentSummary['health'], string> = {
  green: 'Healthy',
  yellow: 'Lagging',
  red: 'Failed',
  unknown: 'Unknown',
};

export function DepartmentHealthCard({ department }: DepartmentHealthCardProps) {
  const stateLabel = STATE_LABEL[department.health];
  const stateDot = STATE_DOT_CLASS[department.health];
  return (
    <div
      className={cn(
        'group/dept relative flex h-full flex-col gap-2 rounded border border-border bg-background',
        'p-3 transition-colors hover:bg-muted/40',
      )}
    >
      <Link
        to="/departments/$deptKey"
        params={{ deptKey: department.key }}
        search={{ tab: 'overview' }}
        className="absolute inset-0 z-0 rounded"
        aria-label={`${department.displayName} — ${stateLabel}`}
      />

      <header className="relative z-10 flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-col">
          <div className="flex items-center gap-1.5">
            <span
              role="img"
              aria-label={stateLabel}
              title={stateLabel}
              className={cn('h-1.5 w-1.5 shrink-0 rounded-full', stateDot)}
            />
            <span className="truncate text-[13px] font-semibold text-foreground">
              {department.displayName}
            </span>
          </div>
          {department.roleDescription ? (
            <span className="mt-0.5 truncate text-[11px] text-muted-foreground">
              {department.roleDescription}
            </span>
          ) : null}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              onClick={(e) => e.stopPropagation()}
              aria-label="More actions"
              className={cn(
                'relative z-10 flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground transition-opacity',
                'opacity-0 group-hover/dept:opacity-100 hover:bg-foreground/5 hover:text-foreground',
                'focus:opacity-100 focus:outline-none',
              )}
            >
              <MoreHorizontal className="h-3.5 w-3.5" aria-hidden />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem asChild>
              <Link
                to="/departments/$deptKey"
                params={{ deptKey: department.key }}
                search={{ tab: 'overview' }}
              >
                Open department
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link
                to="/departments/$deptKey"
                params={{ deptKey: department.key }}
                search={{ tab: 'queue' }}
              >
                Open queue
              </Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      <div className="relative z-10 mt-1 grid grid-cols-2 gap-2">
        <KpiTile
          label="Pending"
          value={fmtCount(department.pendingItems)}
          tone={department.pendingItems > 0 ? 'warning' : 'muted'}
        />
        <KpiTile
          label="Throughput / 24h"
          value={fmtCount(department.throughput24h)}
          tone={department.throughput24h > 0 ? 'success' : 'muted'}
        />
      </div>

      <footer className="relative z-10 mt-auto flex items-center justify-between gap-2 pt-1 text-[11px] text-muted-foreground">
        <span title={department.lastProcessedAt ?? undefined}>
          Last: {fmtRelative(department.lastProcessedAt)}
        </span>
        {!department.workflowRunning ? (
          <span className="text-destructive">Workflow stopped</span>
        ) : null}
      </footer>
    </div>
  );
}
